import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Edit2, Trash2, X } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { Spinner } from './Spinner';
import { useConfirm } from '../contexts/ConfirmDialogContext';
import styles from './SimpleCrudManager.module.css';

interface SimpleItem {
  _id: string;
  name: string;
  description: string;
}

interface SimpleCrudManagerProps {
  title: string;
  subtitle: string;
  apiPath: string;
  itemNoun: string;
  responseKey: string;
}

const initialFormState = { name: '', description: '' };

export function SimpleCrudManager({
  title,
  subtitle,
  apiPath,
  itemNoun,
  responseKey,
}: SimpleCrudManagerProps) {
  const { token } = useAuth();
  const confirm = useConfirm();
  const [items, setItems] = useState<SimpleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<SimpleItem | null>(null);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchItems = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/${apiPath}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setItems(data[responseKey] || []);
    } catch (err) {
      console.error(`Error fetching ${apiPath}:`, err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const openCreateModal = () => {
    setEditingItem(null);
    setFormData(initialFormState);
    setError(null);
    setShowModal(true);
  };

  const openEditModal = (item: SimpleItem) => {
    setEditingItem(item);
    setFormData({ name: item.name, description: item.description || '' });
    setError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData(initialFormState);
    setError(null);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const url = editingItem
        ? `${API_BASE_URL}/api/${apiPath}/${editingItem._id}`
        : `${API_BASE_URL}/api/${apiPath}`;
      const method = editingItem ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to save ${itemNoun.toLowerCase()}`);
      }

      closeModal();
      fetchItems();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      message: `Are you sure you want to delete this ${itemNoun.toLowerCase()}? This cannot be undone.`,
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/${apiPath}/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}) as any);
        throw new Error(data.message || `Failed to delete ${itemNoun.toLowerCase()}`);
      }

      fetchItems();
    } catch (err: any) {
      alert(err.message);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </div>
        <button className={styles.addBtn} onClick={openCreateModal}>
          <Plus size={20} />
          Add {itemNoun}
        </button>
      </header>

      {loading ? (
        <Spinner label={`Loading ${title.toLowerCase()}...`} size="lg" />
      ) : items.length === 0 ? (
        <div className={styles.empty}>
          <p>
            No {title.toLowerCase()} yet. Add your first{' '}
            {itemNoun.toLowerCase()}!
          </p>
        </div>
      ) : (
        <div className={styles.list}>
          {items.map((item) => (
            <div key={item._id} className={styles.row}>
              <div className={styles.rowContent}>
                <strong>{item.name}</strong>
                {item.description && <p>{item.description}</p>}
              </div>
              <div className={styles.actions}>
                <button
                  className={styles.editBtn}
                  onClick={() => openEditModal(item)}
                >
                  <Edit2 size={16} />
                </button>
                <button
                  className={styles.deleteBtn}
                  onClick={() => handleDelete(item._id)}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button className={styles.closeBtn} onClick={closeModal}>
              <X size={20} />
            </button>
            <h2>{editingItem ? `Edit ${itemNoun}` : `Add ${itemNoun}`}</h2>

            {error && <div className={styles.error}>{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label>Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                />
              </div>

              <button
                type="submit"
                className={styles.submitBtn}
                disabled={submitting}
              >
                {submitting
                  ? 'Saving...'
                  : editingItem
                    ? `Update ${itemNoun}`
                    : `Add ${itemNoun}`}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}